import { IDocumentMetadata } from "@multiweb/types/contracts";
import { InvalidArgumentException } from "../../Global/Base/Exceptions";
import { CancellationToken } from "../../Global/Base/Promises/CancellationTokenSource";
import { FileStreamReader } from "../../Global/Base/Streams/FileStreamReader";
import { EDMSException } from "../../OW.Remoting/Basic/OWExceptions";
import { ISignatureDataStream } from "../Crypto/CryptoInterfaces";
import { ISignatureReferenceUriGenerator } from "./ISignatureReferenceUriGenerator";
import { RemotingDocumentReferenceUriGenerator } from "./RemotingDocumentReferenceUriGenerator";

/**
 * Implementace rozhraní streamu poskytujícího data z lokálního souboru
 */
export class LocalDocumentSignatureStream implements ISignatureDataStream {
    private readonly _file: File;
    private readonly _referenceUriGenerator: ISignatureReferenceUriGenerator;

    /**
     * Vytvoří nový stream poskytující data dokumentu z lokálního souboru
     * @param file Nahrávaný soubor
     */
    constructor(file: File) {
        this._file = file;
        this._referenceUriGenerator = new RemotingDocumentReferenceUriGenerator(file.name);
    }

    /**
     * Vygeneruje URI pro referenci ukazující na data v podpisu
     */
    public getReferenceUri(): Promise<string> {
        return this._referenceUriGenerator.getReferenceUri();
    }

    /**
     * Vrací název souboru
     * @returns Název dat
     */
    public async getName(): Promise<string> {
        return this._file.name;
    }

    /**
     * Načítá veškerá metadata ve formátu klíč - hodnota
     * @returns Metadata
     */
    public async getMetadata(): Promise<IDocumentMetadata | null> {
        return Promise.resolve(null);
    }

    /**
     * Načítá celý obsah dokumentu. Stahuje po chuncích definované délky. Po stažení prvního zároveň odesílá
     * první chunk nativní aplikaci a stahuje druhý chunk, atd.
     * @param chunkSize Velikost, po které se mají data načítat
     * @param onChunkDownloaded Callback funkce, která je volaná při načtení části dat
     * @returns Data dokumentu
     */
    public async read(chunkSize: number, onChunkDownloaded: (chunk: ArrayBuffer, isLastChunk: boolean) => Promise<void>, cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        if (!onChunkDownloaded)
            throw new InvalidArgumentException("onChunkDownloaded");

        const contentFullLength = this._file.size;
        let dataStartPosition = 0;
        let lastChunk = false; // příznak, zda se zpracovává poslední chunk
        let evenChunk = false; // příznak, který buffer se používá
        const dataContentBuffer = Array<ArrayBuffer>(2); // buffery pro stahování

        const bufferChooser = (invert: boolean = false): number => {
            const bufferValue = ((!invert ? evenChunk : !evenChunk) ? 0 : 1);
            return bufferValue;
        };
        const getChunkFunction = async (bufferIndex: number) => {
            if (!this._file || this._file.size <= 0)
                throw new EDMSException("Uploaded file on your disk was probably deleted. Please repeat the upload process", "GUI-629112"); //Nahravaný soubor na Vašem disku byl pravděpodobně smazán. Prosím opakujte nahrání souboru.
            if ((contentFullLength !== 0) && (dataStartPosition + chunkSize >= contentFullLength)) {
                chunkSize = contentFullLength - dataStartPosition;
                lastChunk = true;
            }

            if (chunkSize > 0) {
                dataContentBuffer[bufferIndex] = await FileStreamReader.readArrayBuffer(this._file, dataStartPosition, dataStartPosition + chunkSize - 1, cancellationToken);
                dataStartPosition += chunkSize;
            }
        };

        await getChunkFunction(bufferChooser()); // stažení prvního chunku

        do {
            evenChunk = !evenChunk;

            const promises = new Array<Promise<any>>();
            promises.push(onChunkDownloaded(dataContentBuffer[bufferChooser(true)], lastChunk));
            if (!lastChunk) // poslední chunk by se stahoval znovu
                promises.push(getChunkFunction(bufferChooser()));

            await Promise.all(promises); // čekání na odeslání části do nativky a na stažení následující části z DMS

            if (lastChunk && promises.length === 2)
                await onChunkDownloaded(dataContentBuffer[bufferChooser()], true);
        } while (!lastChunk);
    }
}
